import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Stack,
    Chip,
    TextField,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import UserContentDialog from '../../widgets/usercontents/UserContentDialog';
import RewardBadge from '../../widgets/RewardBadge';

import { ReactComponent as LoopCircle } from '../../assets/icons/loop_circle.svg';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import { ReactComponent as XpIcon } from '../../assets/icons/xp.svg';

const UserContentPage = () => {
    const { user } = useContext(UserContext);
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedContent, setSelectedContent] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchContents = async () => {
            try {
                const data = await RequestService.getUserContents(
                    user.company.id
                );
                setContents(Array.isArray(data) ? data : []);
            } catch (err) {
                setError('Erro ao carregar conteúdos.');
                setContents([]);
            } finally {
                setLoading(false);
            }
        };
        fetchContents();
    }, [user.company.id]);

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleSuccessAdd = async ({ id, approved, message }) => {
        setSelectedContent((prev) => {
            if (!prev) return prev;
            const updatedUserContents =
                prev.userContents?.filter((uc) => uc.userContentId !== id) ??
                [];
            return { ...prev, userContents: updatedUserContents };
        });

        try {
            const data = await RequestService.getUserContents(user.company.id);
            setContents(Array.isArray(data) ? data : []);
        } catch (_) {}

        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleOpenEditDialog = (group) => {
        setSelectedContent(group);
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setSelectedContent(null);
    };

    const handleSuccessEdit = async () => {
        const data = await RequestService.getUserContents(user.company.id);
        setContents(data);

        setSnackbarMessage('Grupo atualizado com sucesso!');
        setSnackbarOpen(true);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const renderEmptyState = () => (
        <Box sx={{ textAlign: 'center', mt: 4, color: '#666' }}>
            <Typography variant="h6">Nenhum solicitação pendende.</Typography>
            <Typography variant="body2">
                As solicitações aparecerão aqui.
            </Typography>
        </Box>
    );

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Missões
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Gerenciar
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Gerenciar Missões
                </Typography>
            </Grid>

            {/* <Grid container sx={12} justifyContent={'space-between'}>
                <Grid item sx={6}>
                    <TextField
                        id="outlined-basic"
                        label="Pesquisar"
                        variant="outlined"
                    />
                </Grid>
                <Grid item sx={6}></Grid>
            </Grid> */}

            {contents.length === 0 ? (
                renderEmptyState()
            ) : (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {contents.map((content) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={3}
                            lg={3}
                            key={content.contentId}
                        >
                            <Box
                                sx={{
                                    backgroundColor: '#f7f9fc',
                                    padding: 2,
                                    borderRadius: 2,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    maxWidth: 365,
                                    minWidth: 365,
                                    minHeight: 300,
                                    maxHeight: 300,
                                    margin: '0 auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        color: 'gray',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {content.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        color: 'gray',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                    }}
                                >
                                    {content.description}
                                </Typography>

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    gap={2}
                                    sx={{ mt: 2 }}
                                >
                                    <RewardBadge
                                        icon={
                                            <CoinIcon
                                                style={{
                                                    color: '#134F99',
                                                    fontSize: '24px',
                                                }}
                                            />
                                        }
                                        value="200"
                                    />
                                    <RewardBadge
                                        icon={
                                            <XpIcon
                                                style={{
                                                    color: '#134F99',
                                                    fontSize: '24px',
                                                }}
                                            />
                                        }
                                        value="300"
                                    />
                                </Stack>
                                <Stack direction="row" alignItems="center">
                                    <LoopCircle color="#1969CC" />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'Poppins',
                                            fontSize: '14px',
                                            fontWeight: '400',
                                            color: '#1969CC',
                                            mt: 1,
                                        }}
                                    >
                                        {content.userContents.length}{' '}
                                        Solicitações Pendentes
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    justifyContent="end"
                                    sx={{ mt: 2 }}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() =>
                                            handleOpenEditDialog(content)
                                        }
                                    >
                                        Visualizar solicitações
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}

            <UserContentDialog
                open={isEditDialogOpen}
                onSucessApprove={handleSuccessAdd}
                onClose={(event, reason) => {
                    if (
                        reason === 'backdropClick' ||
                        reason === 'escapeKeyDown'
                    )
                        return;
                    handleCloseEditDialog();
                }}
                content={selectedContent}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserContentPage;
